import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { ArrowRight, Clock, TestTube, Snowflake, Certificate, Thermometer, ShieldCheck, Airplane } from "@phosphor-icons/react";

const FALLBACK_BLOGS = [
  {
    id: "b1",
    slug: "peptide-potency-degradation-timeline",
    title: "How fast do peptides lose potency? The hour-by-hour degradation curve",
    excerpt:
      "Research-grade peptides tested at room temperature vs. 2\u20138\u00b0C. See exactly when potency drops below 90%, 75%, and 50%.",
    category: "Potency",
    read_minutes: 8,
    accent: "from-cyan-500/25 to-teal-500/10",
    icon: Snowflake,
  },
  {
    id: "b2",
    slug: "why-2-to-8-c-preserves-potency",
    title: "Why 2\u20138\u00b0C is the sweet spot for peptide potency",
    excerpt:
      "Below freezing shears molecular bonds. Above 8\u00b0C accelerates hydrolysis. Here's the biochemistry behind the golden window.",
    category: "Cold Chain",
    read_minutes: 6,
    accent: "from-blue-500/25 to-cyan-500/10",
    icon: Thermometer,
  },
  {
    id: "b3",
    slug: "lyophilized-vs-reconstituted-storage",
    title: "Lyophilized vs. reconstituted: two very different shelf lives",
    excerpt:
      "A sealed lyophilized vial can hold 36+ months of potency. Once reconstituted, the clock drops to days. Storage rules for both.",
    category: "Storage",
    read_minutes: 7,
    accent: "from-teal-500/25 to-emerald-500/10",
    icon: TestTube,
  },
  {
    id: "b4",
    slug: "freeze-thaw-cycles-destroy-peptides",
    title: "The freeze-thaw problem: why cycling temperature ruins potency",
    excerpt:
      "Every warm-cold cycle unfolds and refolds the peptide structure. Just 3 cycles can cut bioactivity by up to 40%.",
    category: "Research",
    read_minutes: 5,
    accent: "from-indigo-500/20 to-cyan-500/10",
    icon: Snowflake,
  },
  {
    id: "b5",
    slug: "cryovault-holds-potency-in-transit",
    title: "How CryoVault holds potency during 72-hour international transit",
    excerpt:
      "A field test with three courier routes and a real payload. Continuous 2\u20138\u00b0C, logged every 60 seconds, potency preserved.",
    category: "Field Test",
    read_minutes: 9,
    accent: "from-cyan-500/20 to-blue-500/10",
    icon: Airplane,
  },
  {
    id: "b6",
    slug: "ideal-storage-temperature-by-peptide",
    title: "The ideal storage temperature for every common peptide",
    excerpt:
      "A reference table across common research peptides \u2014 with target ranges, safe excursion limits, and stability windows.",
    category: "Storage",
    read_minutes: 7,
    accent: "from-teal-500/25 to-cyan-500/10",
    icon: Thermometer,
  },
];

function normalise(row) {
  return {
    id: row.id,
    slug: row.slug || row.id,
    title: row.title,
    excerpt: row.excerpt || row.summary || row.description || "",
    category: row.category || row.tag || "Blog",
    read_minutes: row.read_minutes || row.reading_time || 5,
    cover: row.cover_image || row.image || row.thumbnail || null,
  };
}

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export default function Blogs() {
  const [posts, setPosts] = useState(FALLBACK_BLOGS);

  useEffect(() => {
    let cancelled = false;

    const fetchPosts = async () => {
      try {
        let res = await supabase
          .from("blogs")
          .select("*")
          .eq("is_published", true)
          .order("published_at", { ascending: false })
          .limit(6);
        if (res.error) res = await supabase.from("blogs").select("*").limit(6);
        if (!cancelled && !res.error && res.data && res.data.length > 0) {
          setPosts(res.data.map(normalise));
        }
      } catch {
        /* keep fallback */
      }
    };

    fetchPosts();

    const channel = supabase
      .channel("public-blogs")
      .on("postgres_changes", { event: "*", schema: "public", table: "blogs" }, fetchPosts)
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <section id="blogs" data-testid="blogs-section" className="scroll-mt-24 md:scroll-mt-28 lg:scroll-mt-32 bg-cool-white py-14 sm:py-20">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
        <div className="reveal max-w-3xl">
          <div className="overline text-cyan-brand" data-testid="blogs-overline">
            Insights & Research
          </div>
          <h2
            data-testid="blogs-title"
            className="mt-4 font-display text-4xl md:text-5xl lg:text-[54px] font-bold text-navy-deep tracking-tight leading-[1.05]"
          >
            The CryoVault Journal.
          </h2>
          <p className="mt-5 text-[17px] leading-relaxed text-slate-600">
            Cold-chain science, field tests, and practical guidance for researchers who take
            peptide integrity seriously.
          </p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {posts.map((b) => {
            const Icon = b.icon || TestTube;
            return (
              <motion.article
                key={b.id}
                variants={item}
                data-testid={`blog-card-${b.slug}`}
                className="group relative flex flex-col rounded-3xl bg-white border border-[#8A97A8]/25 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-24px_rgba(2,47,107,0.28)] hover:border-cyan-brand/40"
              >
                {/* Cover */}
                <div
                  className={`relative aspect-[16/9] overflow-hidden bg-gradient-to-br ${
                    b.accent || "from-cyan-500/20 to-blue-500/10"
                  }`}
                >
                  {b.cover ? (
                    <img
                      src={b.cover}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <>
                      <div
                        aria-hidden
                        className="absolute inset-0 opacity-70 bg-[radial-gradient(circle_at_30%_30%,rgba(1,207,198,0.25),transparent_55%),radial-gradient(circle_at_70%_70%,rgba(2,47,107,0.15),transparent_60%)]"
                      />
                      <div className="absolute inset-0 grid place-items-center">
                        <div className="h-16 w-16 rounded-2xl bg-white/85 backdrop-blur border border-white/60 grid place-items-center text-navy-deep shadow-[0_10px_30px_-10px_rgba(2,47,107,0.35)]">
                          <Icon size={26} weight="regular" />
                        </div>
                      </div>
                    </>
                  )}
                  <span className="absolute top-4 left-4 inline-flex items-center px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] uppercase tracking-[0.14em] font-mono-data font-semibold text-navy-deep border border-white/60">
                    {b.category}
                  </span>
                </div>

                {/* Body */}
                <div className="flex-1 flex flex-col p-6">
                  <h3 className="font-display font-bold text-navy-deep text-[19px] leading-snug tracking-tight">
                    {b.title}
                  </h3>
                  <p className="mt-2.5 text-[14px] leading-relaxed text-slate-600 line-clamp-3">
                    {b.excerpt}
                  </p>
                  <div className="mt-5 pt-4 border-t border-[#8A97A8]/15 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-mono-data uppercase tracking-[0.12em] text-cool-grey">
                      <Clock size={12} weight="bold" />
                      {b.read_minutes} min read
                    </span>
                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-cyan-brand group-hover:gap-2 transition-all">
                      Read
                      <ArrowRight size={12} weight="bold" />
                    </span>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
